import React, { useEffect, useState } from "react";
import { User } from "../../../shared/models/user/user";
import { ToastContainer } from "react-toastify";
import { useAppDispatch, useAppSelector } from "../../../../redux/store";
import { Message, MessageSections } from "../../../components/message/message";
import BarekamText from "../../../components/text";
import { UserInputs } from "./user-inputs/user-inputs";
import { RecipientInputs } from "./recipient-inputs/recipient-inputs";
import { getCurrentUserData } from "../../../../redux/slices/current-user.slice";
import {
  handleUserSave,
  initSuccessToast,
  mapValidationErrors,
} from "./personal-info.service";
import { SaveResultType } from "../../../shared/enum/save-result-type";
import { StandardDialog } from "../../../components/dialogs/standard-dialog/standard-dialog";
import { PersonalInfoChangePassword } from "./change-password/personal-info-change-password";
import { ActionBarWithSaveButton } from "../user-actionbar/action-bar-with-save-button";
import { Grid } from "@mui/material";
import { useOutletContext } from "react-router-dom";
import { OutletContext } from "../../../../router/router";
import { UserAddress } from "../../../shared/models/user/user-address";
import { getMessage } from "../../../shared/helper-functions";
import { AxiosError } from "axios";

export const PersonalInfo = () => {
  const { matchesPhone } = useAppSelector((state) => state.deviceReducer);

  const [currentUser, setCurrentUser] = useState<User>();
  const [isModified, setIsModified] = useState(false);
  const [changePasswordDialogOpened, setChangePasswordDialogOpened] =
    useState(false);

  const unmodifiedUser = useAppSelector(
    (state) => state.currentUserReducer,
  ).currentUser;

  const { messageCache, languageId } = useAppSelector(
    (state) => state.messagesReducer,
  );

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const outletContext = useOutletContext<OutletContext>();

  const dispatch = useAppDispatch();

  useEffect(() => {
    setCurrentUser(unmodifiedUser);
  }, [unmodifiedUser]);

  useEffect(() => {
    dispatch(getCurrentUserData()).then((data) => {
      if (data.payload) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const user = data.payload.currentUser;
        setCurrentUser({ ...user } as User);
      }
    });
  }, []);

  function handleSaveAction() {
    const successMessage = getMessage(
      "saved",
      MessageSections.USER,
      messageCache,
      languageId,
    );

    if (currentUser && unmodifiedUser) {
      handleUserSave(currentUser, unmodifiedUser)
        .then((result) => {

          if (result.status === SaveResultType.SUCCESS) {
            dispatch(getCurrentUserData()).then(() => {
              initSuccessToast(successMessage);
              setIsModified(false);
              setErrors({});
            });
          } else if (result.payload instanceof AxiosError) {
            setErrors(mapValidationErrors(result.payload));
          }
        })
        .catch((e) => {
          if (e instanceof AxiosError) {
            setErrors(mapValidationErrors(e));
          }
        });
    }
  }

  function handleUserFieldValueChange(value: any, field: string) {
    setIsModified(true);

    setCurrentUser((currentUser) => {
      if (currentUser) {
        return { ...currentUser, [field]: value };
      }
    });
  }

  function handleRecipientFieldValueChange(value: any, field: string) {
    if (currentUser) {
      setIsModified(true);

      if (field === "passportOrIdSerialNumber" && currentUser?.relatedRecipientId?.documentType?.recipientDocumentTypeId === 1) {
        const pattern = /^[A-Z]{2}\d{7}$/;
        if(!pattern.test(value)) {
          setErrors({
            passportOrIdSerialNumber: 'format_error'
          })
        } else {
          setErrors((prevState) => ({
            ...prevState,
            passportOrIdSerialNumber: '',
          }))
        }
      }
  
      if (field === "passportOrIdSerialNumber" && currentUser?.relatedRecipientId?.documentType?.recipientDocumentTypeId === 2) {
        const pattern = /^\d{9}$/;
        if(!pattern.test(value)) {
          setErrors({
            passportOrIdSerialNumber: 'format_error'
          })
        } else {
          setErrors((prevState) => ({
            ...prevState,
            passportOrIdSerialNumber: '',
          }))
        }
      }

      setCurrentUser((prevState) => {
        if (prevState) {
          const relatedRecipientClone = { ...prevState.relatedRecipientId };
          (relatedRecipientClone as any)[field] = value || "";

          return {
            ...prevState,
            relatedRecipientId: relatedRecipientClone,
          } as User;
        }
      });
    }
  }

  function handleRecipientAddressChange(address: UserAddress) {
    if (currentUser) {
      setIsModified(true);

      setCurrentUser((prevState) => {
        if (prevState) {
          const relatedRecipientClone = {
            ...prevState.relatedRecipientId,
            ...address,
          };

          return {
            ...prevState,
            relatedRecipientId: relatedRecipientClone,
          } as User;
        }
      });
    }
  }

  const PrimaryActionText = () => (
    <BarekamText variant={"m"} bold>
      <Message messageKey={"save"} sectionId={MessageSections.USER} />
    </BarekamText>
  );

  return (
    <>
      <ActionBarWithSaveButton
        disabled={!!currentUser}
        primaryActionText={isModified ? <PrimaryActionText /> : undefined}
        onPrimaryActionClick={handleSaveAction}
      />

      <Grid
        container
        columnSpacing={"1.5rem"}
        rowSpacing={"1.5rem"}
        width={matchesPhone ? "100%" : "50%"}
      >
        <UserInputs
          userData={currentUser}
          onChange={handleUserFieldValueChange}
          errors={errors}
          onPasswordChange={() => setChangePasswordDialogOpened(true)}
        />
        {currentUser &&
        !outletContext.isWarehouseWorker &&
        !outletContext.isLocalWarehouseWorker &&
        !outletContext.isAdmin &&
        !outletContext.isPdr ? (
          <RecipientInputs
            recipient={currentUser.relatedRecipientId}
            onChange={handleRecipientFieldValueChange}
            onAddressChange={handleRecipientAddressChange}
            errors={errors}
          />
        ) : (
          <></>
        )}
      </Grid>

      <StandardDialog
        headerLabel={
          <Message
            messageKey={"change_password"}
            sectionId={MessageSections.USER}
          />
        }
        open={changePasswordDialogOpened}
      >
        <PersonalInfoChangePassword
          onClose={() => setChangePasswordDialogOpened(false)}
        />
      </StandardDialog>

      <ToastContainer />
    </>
  );
};
